import { useCallback } from "react"
import { useRouter } from "next/router"

import type {
  CoviOpenVia,
  OpenThreadOptions,
} from "../contexts/CoviContext.types"
import {
  buildSofiaHomeLandingPath,
  buildSofiaHomeThreadPath,
  isSofiaHomePath,
} from "../utils/sofiaHome"

/**
 * Home's navigation surface: the routes that move between the Daily Briefing,
 * a full-page conversation, and the product page a thread was expanded from.
 *
 * Extracted from CoviProvider so the provider stays focused on the thread
 * registry. `homeReturnPath` stays provider state — the org-switch effect
 * clears it — so it is passed in rather than owned here.
 */
export function useCoviHomeSurface({
  orgSlug,
  homeReturnPath,
  setHomeReturnPath,
  residentThreadIds,
  open,
  close,
  openThread,
  setActiveThread,
}: {
  orgSlug: string | null
  homeReturnPath: string | null
  setHomeReturnPath: (path: string | null) => void
  /** Threads that must survive a prune: streaming, drafted, or on screen. */
  residentThreadIds: ReadonlySet<string>
  open: (via?: CoviOpenVia) => void
  close: () => void
  openThread: (opts?: OpenThreadOptions) => string
  setActiveThread: (threadId: string) => void
}) {
  const router = useRouter()

  const showThreadOnHome = useCallback(
    (threadId: string) => {
      if (!threadId) {
        return
      }
      setActiveThread(threadId)
      // Do NOT open() here. Opening flips isOpen=true synchronously, but the
      // full-page surface only engages once the ?sofia_thread= param lands in
      // the URL (async). In that gap `docked` (isOpen && !fullPage) is true, so
      // the docked sidebar panel flashes mid-transition. CoviMount opens the
      // runtime from the URL instead, by which point fullPage is already true.
      const alreadyOnHome = isSofiaHomePath(router.asPath)
      if (!alreadyOnHome) {
        setHomeReturnPath(router.asPath)
      }
      const destination = buildSofiaHomeThreadPath(
        router.asPath,
        orgSlug,
        threadId,
      )
      // Submitting from the Home landing only adds the ?sofia_thread= param to
      // the current page. Route shallowly so the page component stays mounted
      // and the full-page chat swaps in without a route-transition flash. From
      // any other page it's a real navigation to Home, so push normally.
      if (alreadyOnHome) {
        void router.push(destination, undefined, { shallow: true })
      } else {
        void router.push(destination)
      }
    },
    [orgSlug, router, setActiveThread, setHomeReturnPath],
  )

  // Resident threads are protected from the MAX_THREADS prune: evicting one
  // unmounts its view, aborting an in-flight turn or losing an unsent draft.
  const startNewChatOnHome = useCallback(() => {
    const threadId = openThread({ protectedThreadIds: residentThreadIds })
    showThreadOnHome(threadId)
  }, [openThread, residentThreadIds, showThreadOnHome])

  const showDailyBriefing = useCallback(() => {
    const alreadyOnHome = isSofiaHomePath(router.asPath)
    if (!alreadyOnHome) {
      setHomeReturnPath(router.asPath)
    }
    // The briefing is a separate Home destination. CoviMount keeps the thread
    // runtime mounted invisibly while this route is shown, so background work
    // continues and can surface an unread marker when it completes.
    close()
    const destination = buildSofiaHomeLandingPath(router.asPath, orgSlug)
    // Home-to-Home (dropping ?sofia_thread=) keeps the page mounted; from a
    // product page it's a real navigation. Mirrors showThreadOnHome.
    if (alreadyOnHome) {
      void router.push(destination, undefined, { shallow: true })
    } else {
      void router.push(destination)
    }
  }, [close, orgSlug, router, setHomeReturnPath])

  // The Sofia tab is a destination, not a resume. New chat and Daily brief are
  // no longer rows in the sidebar, so the entry that stands where they were has
  // to carry both — and the briefing landing is the one place that does: its
  // composer opens a fresh chat on the first keystroke, with the day's findings
  // below it. Resuming the thread that happened to be open would show neither.
  // The conversation is not lost by going here: it keeps its row in the chat
  // list below, one click away.
  const openHome = useCallback(() => {
    showDailyBriefing()
  }, [showDailyBriefing])

  const returnHomeThreadToPanel = useCallback(() => {
    open("toggle")
    const destination =
      homeReturnPath ?? buildSofiaHomeLandingPath(router.asPath, orgSlug)
    setHomeReturnPath(null)
    void router.push(destination)
  }, [homeReturnPath, open, orgSlug, router, setHomeReturnPath])

  // Closing the full-page surface goes back where the thread was expanded from,
  // with Sofia dismissed — the product page the user was on, not the Home
  // landing. Only a thread with no source page (expanded from Home itself) falls
  // back to the landing.
  const closeHomeThread = useCallback(() => {
    close()
    const destination =
      homeReturnPath ?? buildSofiaHomeLandingPath(router.asPath, orgSlug)
    setHomeReturnPath(null)
    void router.push(destination)
  }, [close, homeReturnPath, orgSlug, router, setHomeReturnPath])

  return {
    showThreadOnHome,
    startNewChatOnHome,
    showDailyBriefing,
    openHome,
    returnHomeThreadToPanel,
    closeHomeThread,
  }
}
